// src/Components/Modules/HR/PayrollGenerate.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Avatar,
  TextField,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import {
  FaCalculator,
  FaLock,
  FaFileCsv,
  FaDownload,
  FaMoneyBillWave,
  FaUsers,
  FaChartLine,
  FaShieldAlt,
  FaSync,
  FaSearch,
  FaFilter,
  FaCheckCircle,
  FaExclamationCircle,
  FaUser,
  FaCalendarAlt,
  FaDollarSign,
  FaFileInvoiceDollar,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: "white",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  transition: "background-color 0.2s ease",
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(0.8, 2),
  minWidth: "auto",
  ...(variant === "lock" && {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    "&:hover": {
      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
    },
  }),
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1.2, 3),
  background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  color: "#1b5e20",
  "&:hover": {
    background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
    boxShadow: "0 6px 20px rgba(67, 233, 123, 0.4)",
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1.2, 3),
  background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
  color: "#b71c1c",
  "&:hover": {
    background: "linear-gradient(135deg, #fee140 0%, #fa709a 100%)",
    boxShadow: "0 6px 20px rgba(250, 112, 154, 0.4)",
  },
}));

const PayrollGenerate = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchPayroll();
  }, [month]);

  const fetchPayroll = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AxiosAuthInstance.get(`hr/payroll/${month}`);
      setData(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      setError("Failed to load payroll data");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const generate = async () => {
    const result = await Swal.fire({
      title: "Generate Payroll?",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p>Are you sure you want to generate payroll for <strong>${formatMonth(month)}</strong>?</p>
          <p style="color: #757575; font-size: 14px;">This will calculate salaries for all employees.</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#43e97b",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Generate",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setGenLoading(true);
      try {
        await AxiosAuthInstance.post("hr/payroll/generate", { month });
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Payroll generated successfully",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchPayroll();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to generate payroll";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setGenLoading(false);
      }
    }
  };

  const lock = async (id) => {
    const result = await Swal.fire({
      title: "Lock Payroll?",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p>Are you sure you want to lock this payroll record?</p>
          <p style="color: #f44336; font-size: 14px;"><strong>Warning:</strong> This action cannot be undone.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#667eea",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Lock",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setActionLoading(id);
      try {
        await AxiosAuthInstance.post(`hr/payroll/lock/${id}`);
        Swal.fire({
          icon: "success",
          title: "Locked!",
          text: "Payroll has been locked successfully",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchPayroll();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to lock payroll";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setActionLoading(null);
      }
    }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("token");
    window.open(`/api/hr/payroll/${month}/export-bank?token=${token}`);
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((item) => {
      const employeeName = item.employee?.employee_name?.toLowerCase() || "";
      const status = item.status?.toLowerCase() || "";
      const searchTerm = search.toLowerCase();

      const matchesSearch = employeeName.includes(searchTerm);
      const matchesFilter = statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesFilter;
    });
  }, [data, search, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate stats
  const stats = useMemo(() => {
    if (!Array.isArray(data)) {
      return { employees: 0, gross: 0, deductions: 0, net: 0 };
    }
    return {
      employees: data.length,
      gross: data.reduce((sum, d) => sum + (d.gross_salary || d.basic_salary + d.allowances || 0), 0),
      deductions: data.reduce(
        (sum, d) =>
          sum +
          (d.tax_deduction || 0) +
          (d.pf_deduction || 0) +
          (d.loan_deduction || 0) +
          (d.attendance_deduction || 0),
        0
      ),
      net: data.reduce((sum, d) => sum + (d.net_salary || 0), 0),
    };
  }, [data]);

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "৳0";
    return `৳${Number(amount).toLocaleString("en-BD", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return "N/A";
    try {
      const date = new Date(monthStr + "-01");
      return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
    } catch {
      return monthStr;
    }
  };

  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "verified" || statusLower === "locked") {
      return (
        <Chip
          icon={<FaCheckCircle style={{ fontSize: 12 }} />}
          label="Locked"
          size="small"
          sx={{
            bgcolor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#4caf50" },
          }}
        />
      );
    }
    return (
      <Chip
        icon={<FaExclamationCircle style={{ fontSize: 12 }} />}
        label="Pending"
        size="small"
        sx={{
          bgcolor: "#fff3e0",
          color: "#ef6c00",
          fontWeight: 600,
          "& .MuiChip-icon": { color: "#ff9800" },
        }}
      />
    );
  };

  const getInitials = (name) => {
    if (!name) return "N/A";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      "#667eea", "#764ba2", "#f093fb", "#4facfe",
      "#43e97b", "#fa709a", "#fee140", "#30cfd0",
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const calculateDeductions = (row) => {
    return (
      (row.tax_deduction || 0) +
      (row.pf_deduction || 0) +
      (row.loan_deduction || 0) +
      (row.attendance_deduction || 0)
    );
  };

  const calculateGross = (row) => {
    return row.gross_salary || (row.basic_salary || 0) + (row.allowances || 0);
  };

  if (loading && data.length === 0) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="Payroll Management" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Payroll Management" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaMoneyBillWave style={{ marginRight: 10, verticalAlign: "middle" }} />
              Payroll Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Generate and manage employee payroll for {formatMonth(month)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh Data">
              <IconButton
                onClick={fetchPayroll}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <FaSync />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
  <Grid item xs={12} sm={6} md={3}>
    <StatCard elevation={0}>
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          bgcolor: "#e3f2fd",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <FaUsers size={28} color="#2196f3" />
      </Box>
      <Typography variant="h3" fontWeight={700} color="#1565c0" sx={{ mb: 0.5 }}>
        {stats.employees}
      </Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        Total Employees
      </Typography>
    </StatCard>
  </Grid>
  <Grid item xs={12} sm={6} md={3}>
    <StatCard elevation={0}>
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          bgcolor: "#e8f5e9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <FaChartLine size={28} color="#4caf50" />
      </Box>
      <Typography variant="h4" fontWeight={700} color="#2e7d32" sx={{ mb: 0.5 }}>
        {formatCurrency(stats.gross)}
      </Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        Total Gross
      </Typography>
    </StatCard>
  </Grid>
  <Grid item xs={12} sm={6} md={3}>
    <StatCard elevation={0}>
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          bgcolor: "#ffebee",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <FaFileInvoiceDollar size={28} color="#f44336" />
      </Box>
      <Typography variant="h4" fontWeight={700} color="#c62828" sx={{ mb: 0.5 }}>
        {formatCurrency(stats.deductions)}
      </Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        Total Deductions
      </Typography>
    </StatCard>
  </Grid>
  <Grid item xs={12} sm={6} md={3}>
    <StatCard elevation={0}>
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          bgcolor: "#fff3e0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <FaDollarSign size={28} color="#ff9800" />
      </Box>
      <Typography variant="h4" fontWeight={700} color="#ef6c00" sx={{ mb: 0.5 }}>
        {formatCurrency(stats.net)}
      </Typography>
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        Total Net Pay
      </Typography>
    </StatCard>
  </Grid>
</Grid>

      {/* Controls */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaCalendarAlt style={{ color: "#757575" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          />
          <TextField
            placeholder="Search by employee name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch style={{ color: "#757575" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          />
          <TextField
            select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="verified">Locked</MenuItem>
          </TextField>
          <Stack direction="row" spacing={1}>
            <PrimaryButton
              variant="contained"
              onClick={generate}
              disabled={genLoading}
              startIcon={genLoading ? null : <FaCalculator />}
            >
              {genLoading ? "Generating..." : "Generate"}
            </PrimaryButton>
            <SecondaryButton
              variant="contained"
              onClick={exportCsv}
              startIcon={<FaFileCsv />}
            >
              Export CSV
            </SecondaryButton>
          </Stack>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <StyledTableCell width={80}>SL</StyledTableCell>
              <StyledTableCell>Employee</StyledTableCell>
              <StyledTableCell align="right">Gross Salary</StyledTableCell>
              <StyledTableCell align="right">Deductions</StyledTableCell>
              <StyledTableCell align="right">Net Salary</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from(new Array(5)).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton width={40} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Skeleton variant="circular" width={40} height={40} />
                      <Skeleton width={120} />
                    </Box>
                  </TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                </TableRow>
              ))
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <FaUser style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No payroll records found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search || statusFilter !== "all"
                        ? "Try adjusting your filters"
                        : "Generate payroll to get started"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const employeeName = row.employee?.employee_name || "N/A";
                const gross = calculateGross(row);
                const deductions = calculateDeductions(row);
                const net = row.net_salary || 0;
                const statusLower = row.status?.toLowerCase();
                const isLocked = statusLower === "verified" || statusLower === "locked";

                return (
                  <StyledTableRow key={row.id || index} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          sx={{
                            bgcolor: getColorFromName(employeeName),
                            width: 40,
                            height: 40,
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(employeeName)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {employeeName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.employee?.designation || "Employee"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color="#2e7d32">
                        {formatCurrency(gross)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color="#c62828">
                        {formatCurrency(deductions)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={700} color="#ef6c00">
                        {formatCurrency(net)}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(row.status)}</TableCell>
                    <TableCell align="center">
                      {isLocked ? (
                        <Chip
                          icon={<FaLock style={{ fontSize: 12 }} />}
                          label="Locked"
                          size="small"
                          sx={{
                            bgcolor: "#e8f5e9",
                            color: "#2e7d32",
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        <Tooltip title="Lock Payroll">
                          <span>
                            <ActionButton
                              variant="lock"
                              size="small"
                              disabled={actionLoading === row.id}
                              onClick={() => lock(row.id)}
                              startIcon={actionLoading === row.id ? null : <FaLock />}
                            >
                              {actionLoading === row.id ? "Locking..." : "Lock"}
                            </ActionButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {!loading && filtered.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: 1,
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                paddingRight: 2,
              },
            }}
          />
        )}
      </StyledTableContainer>
    </Box>
  );
};

export default PayrollGenerate;